import { ColumnDef, FilterFn } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import { ArrowUpDown, Edit, Trash2, Loader2 } from "lucide-react";

const multiFieldFilter: FilterFn<StoreFormatOperatorRow> = (
  row,
  _columnId,
  filterValue
) => {
  const search = (filterValue as string).toLowerCase();
  return (
    row.original.storeName.toLowerCase().includes(search) ||
    row.original.operatorName.toLowerCase().includes(search) ||
    row.original.formatName.toLowerCase().includes(search)
  );
};

export interface StoreFormatOperatorRow {
  storeId: number;
  mediaFormatId: number;
  operatorId: number;
  storeName: string;
  formatName: string;
  operatorName: string;
  volume: number;
  assignmentDate: string | null;
  createdAt?: string;
}

export interface StoreFormatOperatorColumnHandlers {
  onEditOperator: (row: StoreFormatOperatorRow) => void;
  onEditVolume: (row: StoreFormatOperatorRow) => void;
  onDelete: (row: StoreFormatOperatorRow) => void;
  isDeleting?: boolean;
}

export function createStoreFormatOperatorColumns(
  handlers: StoreFormatOperatorColumnHandlers
): ColumnDef<StoreFormatOperatorRow>[] {
  const { onEditOperator, onEditVolume, onDelete, isDeleting } = handlers;

  return [
    {
      accessorKey: "storeName",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-8 px-2"
        >
          Store
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => row.original.storeName,
      filterFn: multiFieldFilter,
    },
    {
      accessorKey: "formatName",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-8 px-2"
        >
          Media Format
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => row.original.formatName,
    },
    {
      accessorKey: "operatorName",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-8 px-2"
        >
          Assigned Operator
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => row.original.operatorName,
    },
    {
      accessorKey: "volume",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-8 px-2"
        >
          Volume
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => {
        const volume = row.original.volume;
        return (
          <div className="flex items-center space-x-2">
            <span className={volume === 0 ? "text-red-500" : ""}>
              {volume}
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                onEditVolume(row.original);
              }}
              title="Edit volume"
            >
              <Edit className="h-4 w-4" />
            </Button>
          </div>
        );
      },
    },
    {
      accessorKey: "assignmentDate",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-8 px-2"
        >
          Assignment Date
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => {
        const date = row.original.assignmentDate;
        return date ? new Date(date).toLocaleDateString() : "N/A";
      },
      sortingFn: (a, b) => {
        const aVal = a.original.assignmentDate
          ? new Date(a.original.assignmentDate).getTime()
          : 0;
        const bVal = b.original.assignmentDate
          ? new Date(b.original.assignmentDate).getTime()
          : 0;
        return aVal - bVal;
      },
    },
    {
      id: "actions",
      header: () => <div className="text-left">Actions</div>,
      cell: ({ row }) => (
        <div className="flex items-center space-x-2">
          <Button
            size="sm"
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              onEditOperator(row.original);
            }}
            title="Edit operator"
          >
            <Edit className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(row.original);
            }}
            disabled={isDeleting}
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
            title="Delete assignment"
          >
            {isDeleting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4" />
            )}
          </Button>
        </div>
      ),
      enableSorting: false,
      enableHiding: false,
    },
  ];
}
